import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  DryRunPayload,
  DryRunResponse,
  ReemitPayload,
  ReemitResponse,
  TriggerTaskPayload,
  TriggerTaskResponse,
  ValidatorKind,
  WebhookTestFirePayload,
  WebhookTestFireResponse,
  WorkflowToggleResponse,
} from '@/client/types/workflows';
import { unwrapWorkflows, workflowKeys } from './workflows.query';

const BASE = '/api/admin/v2/workflows';

// ─── n8n activate / deactivate ────────────────────────────────────────────────

export const useActivateWorkflowMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (wid: string): Promise<WorkflowToggleResponse> => {
    const raw = await request<APIResponse<WorkflowToggleResponse> | WorkflowToggleResponse>(
      `${BASE}/${wid}/activate`,
      'POST',
      {},
    );
    return unwrapWorkflows(raw);
  }, 'Failed to activate workflow');
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, wid) => {
      qc.invalidateQueries({ queryKey: workflowKeys.all });
      qc.invalidateQueries({ queryKey: workflowKeys.detail(wid) });
    },
  });
};

export const useDeactivateWorkflowMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (wid: string): Promise<WorkflowToggleResponse> => {
    const raw = await request<APIResponse<WorkflowToggleResponse> | WorkflowToggleResponse>(
      `${BASE}/${wid}/deactivate`,
      'POST',
      {},
    );
    return unwrapWorkflows(raw);
  }, 'Failed to deactivate workflow');
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, wid) => {
      qc.invalidateQueries({ queryKey: workflowKeys.all });
      qc.invalidateQueries({ queryKey: workflowKeys.detail(wid) });
    },
  });
};

// ─── Scheduler trigger ────────────────────────────────────────────────────────

export const useTriggerSchedulerTaskMutation = () => {
  const request = useClientStore((s) => s.request);
  const fn = withErrorHandling(
    async (args: {
      taskName: string;
      body?: TriggerTaskPayload;
    }): Promise<TriggerTaskResponse> => {
      const raw = await request<APIResponse<TriggerTaskResponse> | TriggerTaskResponse>(
        `${BASE}/scheduler/tasks/${encodeURIComponent(args.taskName)}/trigger`,
        'POST',
        args.body ?? {},
      );
      return unwrapWorkflows(raw);
    },
    'Failed to trigger task',
  );
  return useMutation({ mutationFn: fn });
};

// ─── Validator actions ────────────────────────────────────────────────────────

export const useReemitValidationsMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { kind: ValidatorKind; body: ReemitPayload }): Promise<ReemitResponse> => {
      const raw = await request<APIResponse<ReemitResponse> | ReemitResponse>(
        `${BASE}/validator/${args.kind}/reemit`,
        'POST',
        args.body,
      );
      return unwrapWorkflows(raw);
    },
    'Failed to reemit validations',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: [...workflowKeys.all, 'validator', args.kind] });
    },
  });
};

export const useDryRunValidationMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { kind: ValidatorKind; body: DryRunPayload }): Promise<DryRunResponse> => {
      const raw = await request<APIResponse<DryRunResponse> | DryRunResponse>(
        `${BASE}/validator/${args.kind}/dry-run`,
        'POST',
        args.body,
      );
      return unwrapWorkflows(raw);
    },
    'Failed to enqueue dry-run',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: [...workflowKeys.all, 'validator', args.kind] });
    },
  });
};

// ─── Webhook test-fire ────────────────────────────────────────────────────────

export const useWebhookTestFireMutation = () => {
  const request = useClientStore((s) => s.request);
  const fn = withErrorHandling(
    async (args: {
      route: string;
      body?: WebhookTestFirePayload;
    }): Promise<WebhookTestFireResponse> => {
      const raw = await request<APIResponse<WebhookTestFireResponse> | WebhookTestFireResponse>(
        `${BASE}/webhooks/${encodeURIComponent(args.route)}/test-fire`,
        'POST',
        args.body ?? {},
      );
      return unwrapWorkflows(raw);
    },
    'Failed to test-fire webhook',
  );
  return useMutation({ mutationFn: fn });
};
